// components/agent/AvailableLeadModal.js
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, MapPin, Phone, Lock } from 'react-feather';
import PropertyDetailsModal from '../common/PropertyDetailsModal';
import AuthService from '../../services/authService';
import darkIcon from '../../assets/dark-iconp.png';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const AvailableLeadModal = ({ user, isOpen, onClose, onClaim, onNotForMe, lightMode = true }) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyLoading, setPropertyLoading] = useState(false);

  // Lauft bot transcript (read-only) — backend: GET /messages/lauft-bot/:userId
  const [botMessages, setBotMessages] = useState([]);
  const [botLoading, setBotLoading] = useState(false);
  const [showBotTranscript, setShowBotTranscript] = useState(false);

  const formatDisplayName = (name) => {
    if (!name) return 'Anonymous';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
  };
  
  // Light mode permanent (dark mode removed from portal)
  const [isLightMode] = useState(true);

  if (!isOpen || !user) return null;

  const tierEmoji = {
    flex: '⚪',
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return 'Not provided';
    // Show last 4 digits: (***) ***-1234
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(***) ***-${cleaned.slice(-4)}`;
    }
    return '(***) ***-****';
  };

  // Detects whether the property already has the heavy fields the modal needs.
  // The available-leads endpoint returns lean property projections (no location
  // coordinates, no mlsData, etc.) — so when the agent clicks one, we backfill
  // the full document. If the property already has the rich data (e.g. when
  // this modal is reused from a context that ships full docs), we skip the fetch.
  const isPropertyHydrated = (p) => {
    if (!p) return false;
    const hasGeo =
      Array.isArray(p.location?.coordinates) && p.location.coordinates.length === 2;
    const hasMls = !!(p.mlsData && Object.keys(p.mlsData).length > 0);
    const hasEnriched = !!(p.enrichedData && Object.keys(p.enrichedData).length > 0);
    return hasGeo || hasMls || hasEnriched;
  };

  const handlePropertyClick = async (property) => {
    // Open the modal immediately so the agent sees instant feedback.
    setSelectedProperty(property);
    setShowPropertyModal(true);

    // If the data is already rich, no fetch needed.
    if (isPropertyHydrated(property)) return;

    const propertyId = property?._id || property?.id;
    if (!propertyId) return;

    try {
      setPropertyLoading(true);
      const response = await fetch(`${API_URL}/properties/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });

      if (!response.ok) {
        // If the fetch fails, leave the lean property in place — modal still
        // renders, just without the Location / extended Details data.
        console.warn('Failed to hydrate property', propertyId, response.status);
        return;
      }

      const data = await response.json();
      // Backend may return { success, data } or the property directly — handle both.
      const full = data?.data || data?.property || data;
      if (full && (full._id || full.id)) {
        setSelectedProperty(full);
      }
    } catch (err) {
      console.error('Error hydrating property:', err);
    } finally {
      setPropertyLoading(false);
    }
  };

  // Fetch this lead's Lauft assistant conversation (read-only).
  // Backend guards access: agents only get users they own or who sit in one
  // of their active pools.
  const fetchBotConversation = async (userId) => {
    if (!userId) return;
    setBotLoading(true);
    setShowBotTranscript(true);
    try {
      const res = await fetch(`${API_URL}/messages/lauft-bot/${userId}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = res.ok ? await res.json() : { data: { messages: [] } };
      setBotMessages(json.data?.messages || []);
    } catch (err) {
      console.error('Error fetching bot conversation:', err);
      setBotMessages([]);
    } finally {
      setBotLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  };

  const calculateMonthlyIncome = (income, frequency) => {
    if (!income) return null;
    
    switch(frequency?.toLowerCase()) {
      case 'weekly':
        return Math.round(income * 4.33); // Average weeks per month
      case 'bi-weekly':
      case 'biweekly':
      case 'bi weekly':
        return Math.round(income * 2.17); // Average bi-weekly periods per month
      case 'monthly':
      default:
        return income;
    }
  };

  const formatLivingSituation = (situation) => {
    const mapping = {
      'renting': 'Currently Renting',
      'rent': 'Currently Renting',
      'livingwithfamily': 'Family',
      'family': 'Family',
      'ownhome': 'Homeowner',
      'own': 'Homeowner'
    };
    return mapping[situation] || situation;
  };

  const formatAmenity = (amenityId) => {
    const mapping = {
      'hardwood': { emoji: '🪵', label: 'Hardwood Floors' },
      'aircon': { emoji: '❄️', label: 'Central Air/AC' },
      'garage': { emoji: '🚗', label: 'Garage' },
      'appliances': { emoji: '✨', label: 'Stainless Steel' },
      'view': { emoji: '🏔️', label: 'Scenic Views' },
      'pool': { emoji: '🏊', label: 'Swimming Pool' },
      'fireplace': { emoji: '🔥', label: 'Fireplace' },
      'balcony': { emoji: '🌅', label: 'Balcony/Patio' },
      'garden': { emoji: '🌸', label: 'Garden/Yard' },
      'walkincloset': { emoji: '👗', label: 'Walk-in Closet' },
      'security': { emoji: '🔒', label: 'Security System' },
      'gym': { emoji: '💪', label: 'Home Gym' },
      'office': { emoji: '💻', label: 'Home Office' }
    };
    return mapping[amenityId] || { emoji: '', label: amenityId };
  };

  // Get styles based on current theme
  const styles = getStyles(isLightMode);

  return createPortal(
    <>
      <div 
        className={lightMode ? 'light-mode' : ''} 
        style={styles.overlay} 
        onClick={onClose}
      >
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.header}>
            <div style={{ flex: 1 }}>
              <h2 style={styles.title}>{formatDisplayName(user.name)}</h2>
              <p style={{...styles.subtitle, textAlign: 'left'}}>User Preview</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => onClaim && onClaim(user._id)}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#111',
                  border: '1px solid #e5e7eb',
                  padding: '9px 18px',
                  borderRadius: '10px',
                  fontSize: '0.88rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.005em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Claim User
              </button>
              <button
                onClick={() => onNotForMe && onNotForMe(user._id)}
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.06)',
                  color: '#b91c1c',
                  border: '1px solid rgba(220, 38, 38, 0.18)',
                  padding: '9px 18px',
                  borderRadius: '10px',
                  fontSize: '0.88rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.005em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.11)';
                  e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.18)';
                }}
              >
                Decline User
              </button>
              <button onClick={onClose} style={styles.closeButton}>
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={styles.content}>
            {/* Unlock Info Banner */}
            <div style={styles.unlockBanner}>
              <Lock size={16} style={{ marginRight: '8px' }} />
              Claim this user to unlock full contact information and connect directly on or off platform
            </div>

            {/* Lead Information */}
            <div style={styles.section}>
              <div style={styles.leadInfo}>
                <div style={styles.leadBadge}>
                  <span style={styles.leadTierEmoji}>
                    {tierEmoji[user.leadTier?.toLowerCase()] || '❓'}
                  </span>
                  <span style={styles.leadTierText}>
                    {user.leadTier?.toUpperCase() || 'UNASSIGNED'}
                  </span>
                </div>

                {user.leadStatus && (
                  <div style={styles.leadStatusBadge}>
                    Status: {user.leadStatus}
                  </div>
                )}
              </div>
            </div>

            {/* Limited Contact Info - Phone Only (Masked) */}
            <div style={styles.section}>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <Phone size={16} style={styles.infoIcon} />
                  <div>
                    <p style={styles.infoLabel}>Phone</p>
                    <p style={styles.infoValue}>***-***-****</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <MapPin size={16} style={styles.infoIcon} />
                  <div>
                    <p style={styles.infoLabel}>Location</p>
                    <p style={styles.infoValue}>{user.location || 'Not provided'}</p>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <Calendar size={16} style={styles.infoIcon} />
                  <div>
                    <p style={styles.infoLabel}>Registered</p>
                    <p style={styles.infoValue}>
                      {formatDate(user.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* User Stats */}
            <div style={styles.section}>
              <div style={styles.statsContainer}>
                <div style={styles.statBox}>
                  <div style={styles.statNumber}>
                    {user.likes?.length || 0}
                  </div>
                  <div style={styles.statLabel}>
                    LIKED
                  </div>
                </div>

                <div style={styles.statDivider} />

                <div style={styles.statBox}>
                  <div style={styles.statNumber}>
                    {user.dislikes?.length || 0}
                  </div>
                  <div style={styles.statLabel}>
                    PASSED
                  </div>
                </div>

                <div style={styles.statDivider} />

                <div style={styles.statBox}>
                  <div style={styles.statNumber}>
                    {(user.likes?.length || 0) + (user.dislikes?.length || 0)}
                  </div>
                  <div style={styles.statLabel}>
                    TOTAL
                  </div>
                </div>
              </div>
            </div>

            {/* Lauft Assistant Conversation (read-only) */}
            {user.hasBotChat && (
              <div style={styles.section}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <img src={darkIcon} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} />
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                    Lauft Assistant Conversation
                  </span>
                </div>
                {!showBotTranscript ? (
                  <button
                    onClick={() => fetchBotConversation(user._id)}
                    style={styles.viewBotButton}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37, 99, 235, 0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                  >
                    View assistant chat history
                  </button>
                ) : botLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                    Loading conversation…
                  </div>
                ) : botMessages.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                    This user hasn't chatted with the assistant yet.
                  </div>
                ) : (
                  <div style={styles.botTranscript}>
                    {botMessages.map((msg, idx) => {
                      const isBot = msg.sender == null; // bot messages are stored with sender: null
                      return (
                        <div
                          key={msg._id || idx}
                          style={{ ...styles.botRow, justifyContent: isBot ? 'flex-start' : 'flex-end' }}
                        >
                          <div style={isBot ? styles.botBubble : styles.botUserBubble}>
                            <div style={{ ...styles.botBubbleText, ...(isBot ? {} : styles.botUserText) }}>
                              {msg.content}
                            </div>
                            <div style={{ ...styles.botBubbleTime, ...(isBot ? {} : { color: '#fff' }) }}>
                              {msg.createdAt
                                ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Onboarding Data */}
            {user.onboardingData && (
              <div style={styles.section}>
                <div style={styles.onboardingGrid}>
                  {user.onboardingData.buyerType && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Moving As</span>
                      <span style={styles.onboardingValue}>
                        <strong>{user.onboardingData.buyerType === 'solo' ? 'Individual' : 'Household'}</strong>
                      </span>
                    </div>
                  )}
                  {user.onboardingData.livingSituation && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Living Situation</span>
                      <span style={styles.onboardingValue}>
                        {formatLivingSituation(user.onboardingData.livingSituation)}
                      </span>
                    </div>
                  )}
                  {user.onboardingData.currentExpense !== undefined && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Current Living Expense</span>
                      <span style={styles.onboardingValue}>
                        {formatCurrency(user.onboardingData.currentExpense)}
                      </span>
                    </div>
                  )}
                  {user.onboardingData.budgetExpense !== undefined && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Dream Budget</span>
                      <span style={styles.onboardingValue}>
                        {formatCurrency(user.onboardingData.budgetExpense)}
                      </span>
                    </div>
                  )}
                  {user.onboardingData.monthlyIncome && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>
                        {user.onboardingData.buyerType === 'household' ? 'Household Income' : 'Monthly Income'}
                      </span>
                      <span style={styles.onboardingValue}>
                        {formatCurrency(calculateMonthlyIncome(
                          user.onboardingData.monthlyIncome,
                          user.onboardingData.incomeFrequency
                        ))}
                      </span>
                    </div>
                  )}
                  {user.onboardingData.creditScore && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Credit Score</span>
                      <span style={styles.onboardingValue}>
                        {user.onboardingData.creditScore}
                      </span>
                    </div>
                  )}
                  {user.onboardingData.amenities && user.onboardingData.amenities.length > 0 && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Desired Amenities</span>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '8px'
                      }}>
                        {user.onboardingData.amenities.map((amenityId, index) => {
                          const formatted = formatAmenity(amenityId);
                          return (
                            <div key={index} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '50%',
                                fontSize: '14px'
                              }}>
                                {formatted.emoji}
                              </span>
                              <span style={styles.onboardingValue}>
                                {formatted.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {user.onboardingData.completedAt && (
                    <div style={styles.onboardingItem}>
                      <span style={styles.onboardingLabel}>Completed At</span>
                      <span style={styles.onboardingValue}>
                        {formatDate(user.onboardingData.completedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Properties Liked */}
            {user.likes && user.likes.length > 0 && (
              <div style={styles.section}>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#374151',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  Properties Liked ({user.likes.length})
                </div>
                <div style={styles.propertyGrid}>
                  {user.likes.slice(0, 6).map((property, index) => {
                    // Check if property is populated (object) or just an ID (string)
                    if (typeof property === 'string' || !property.address) {
                      return null; // Skip unpopulated properties
                    }
                    
                    return (
                      <div
                        key={property._id || index}
                        style={styles.propertyCard}
                        onClick={() => handlePropertyClick(property)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <div style={{ position: 'relative' }}>
                          {(property.mainImage || (property.images && property.images.length > 0)) ? (
                            <img
                              src={property.mainImage || property.images[0]}
                              alt={property.address || 'Property'}
                              style={{ ...styles.propertyImage, display: 'block' }}
                            />
                          ) : (
                            <div style={{
                              ...styles.propertyImage,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.3)'
                            }}>
                              No Image
                            </div>
                          )}
                          {property.propertyStatus === 'Off Market' && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              padding: '3px 8px',
                              background: 'rgba(17, 24, 39, 0.78)',
                              backdropFilter: 'blur(4px)',
                              WebkitBackdropFilter: 'blur(4px)',
                              color: '#ffffff',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              borderRadius: '4px',
                              lineHeight: 1.4,
                            }}>
                              Status Change
                            </div>
                          )}
                        </div>
                        <div style={styles.propertyInfo}>
                          <div style={styles.propertyAddress}>
                            {property.address || 'Address not available'}
                          </div>
                          {property.csz && (
                            <div style={styles.propertyLocality}>{property.csz}</div>
                          )}
                          <div style={styles.propertyPrice}>
                            {property.price || formatCurrency(property.numericPrice)}
                          </div>
                          <div style={styles.propertyDetails}>
                            {property.bedrooms || 0} bed • {property.bathrooms || 0} bath
                            {property.sqft && ` • ${property.sqft.toLocaleString()} sqft`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {user.likes.length > 6 && (
                  <div style={styles.moreProperties}>
                    + {user.likes.length - 6} more properties
                  </div>
                )}
              </div>
            )}

            {/* Properties Disliked */}
            {user.dislikes && user.dislikes.length > 0 && (
              <div style={styles.section}>
                <div style={{
                  fontSize: '0.875rem',
                  color: '#374151',
                  marginBottom: '12px',
                  fontWeight: '600'
                }}>
                  Properties Passed ({user.dislikes.length})
                </div>
                <div style={styles.propertyGrid}>
                  {user.dislikes.slice(0, 6).map((property, index) => {
                    // Check if property is populated (object) or just an ID (string)
                    if (typeof property === 'string' || !property.address) {
                      return null; // Skip unpopulated properties
                    }
                    
                    return (
                      <div
                        key={property._id || index}
                        style={styles.propertyCard}
                        onClick={() => handlePropertyClick(property)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <div style={{ position: 'relative' }}>
                          {(property.mainImage || (property.images && property.images.length > 0)) ? (
                            <img
                              src={property.mainImage || property.images[0]}
                              alt={property.address || 'Property'}
                              style={{ ...styles.propertyImage, display: 'block' }}
                            />
                          ) : (
                            <div style={{
                              ...styles.propertyImage,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'rgba(255, 255, 255, 0.3)'
                            }}>
                              No Image
                            </div>
                          )}
                          {property.propertyStatus === 'Off Market' && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              padding: '3px 8px',
                              background: 'rgba(17, 24, 39, 0.78)',
                              backdropFilter: 'blur(4px)',
                              WebkitBackdropFilter: 'blur(4px)',
                              color: '#ffffff',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              borderRadius: '4px',
                              lineHeight: 1.4,
                            }}>
                              Status Change
                            </div>
                          )}
                        </div>
                        <div style={styles.propertyInfo}>
                          <div style={styles.propertyAddress}>
                            {property.address || 'Address not available'}
                          </div>
                          {property.csz && (
                            <div style={styles.propertyLocality}>{property.csz}</div>
                          )}
                          <div style={styles.propertyPrice}>
                            {property.price || formatCurrency(property.numericPrice)}
                          </div>
                          <div style={styles.propertyDetails}>
                            {property.bedrooms || 0} bed • {property.bathrooms || 0} bath
                            {property.sqft && ` • ${property.sqft.toLocaleString()} sqft`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {user.dislikes.length > 6 && (
                  <div style={styles.moreProperties}>
                    + {user.dislikes.length - 6} more properties
                  </div>
                )}
              </div>
            )}

            {/* Bottom Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              paddingTop: '32px',
              paddingBottom: '16px'
            }}>
              <button
                onClick={() => onClaim && onClaim(user._id)}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#111',
                  border: '1px solid #e5e7eb',
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '0.92rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  letterSpacing: '-0.005em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Claim User
              </button>

              {/* Explicit pass — badges the lead as "viewed + declined" in the list */}
              <button
                onClick={() => onNotForMe && onNotForMe(user._id)}
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.06)',
                  color: '#b91c1c',
                  border: '1px solid rgba(220, 38, 38, 0.18)',
                  padding: '10px 22px',
                  borderRadius: '10px',
                  fontSize: '0.92rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  letterSpacing: '-0.005em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.11)';
                  e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.18)';
                }}
              >
                Decline User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Property Details Modal */}
      {showPropertyModal && selectedProperty && (
        <PropertyDetailsModal
          property={selectedProperty}
          isOpen={showPropertyModal}
          onClose={() => {
            setShowPropertyModal(false);
            setSelectedProperty(null);
          }}
        />
      )}
    </>,
    document.body
  );
};

const getStyles = (isLightMode) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    background: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '1px solid #f0f0f0',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '700',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.2s',
  },
  content: {
    padding: '24px',
  },
  unlockBanner: {
    backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.1)',
    border: isLightMode ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: isLightMode ? '#111' : '#a5b4fc',
    fontSize: '0.875rem',
    textAlign: 'center',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    marginBottom: '24px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  infoIcon: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.5)' : 'rgba(255, 255, 255, 0.5)',
    marginTop: '2px',
  },
  infoLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.75rem',
    margin: '0 0 4px 0',
  },
  infoValue: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.875rem',
    margin: 0,
  },
  leadInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'center',
  },
  leadBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: isLightMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.2)',
    border: isLightMode ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid #6366f1',
    padding: '8px 16px',
    borderRadius: '8px',
  },
  leadTierEmoji: {
    fontSize: '1.25rem',
  },
  leadTierText: {
    color: '#111',
    fontSize: '0.875rem',
    fontWeight: '600',
    letterSpacing: '0.02em',
  },
  leadStatusBadge: {
    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.08)' : 'rgba(255, 255, 255, 0.05)',
    padding: '8px 16px',
    borderRadius: '8px',
    color: isLightMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.875rem',
  },
  onboardingGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  onboardingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
  },
  onboardingLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
  },
  onboardingValue: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
    border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : 'none',
    borderRadius: '8px',
    padding: '24px',
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '700',
    color: isLightMode ? '#1a1a1a' : '#ffffff',
  },
  statLabel: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  statDivider: {
    width: '1px',
    height: '60px',
    backgroundColor: isLightMode ? 'rgba(26, 26, 26, 0.2)' : 'rgba(255, 255, 255, 0.2)',
  },
  propertyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '16px',
  },
  propertyCard: {
    backgroundColor: isLightMode ? '#f9f8f5' : 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    overflow: 'hidden',
    border: isLightMode ? '1px solid rgba(26, 26, 26, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.2s, border-color 0.2s',
    cursor: 'pointer',
  },
  propertyImage: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
  },
  propertyInfo: {
    padding: '12px',
  },
  propertyAddress: {
    color: isLightMode ? '#1a1a1a' : '#ffffff',
    fontSize: '0.9rem',
    fontWeight: '600',
    marginBottom: '4px',
  },
  propertyLocality: {
    color: isLightMode ? '#6b7280' : 'rgba(255, 255, 255, 0.5)',
    fontSize: '0.78rem',
    marginBottom: '6px',
  },
  propertyPrice: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.85rem',
  },
  propertyDetails: {
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.8rem',
    marginTop: '4px',
  },
  moreProperties: {
    marginTop: '12px',
    color: isLightMode ? 'rgba(26, 26, 26, 0.6)' : 'rgba(255, 255, 255, 0.6)',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  // Bot transcript — kept identical to AdminUserModal so both viewers match
  viewBotButton: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1d4ed8',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  botTranscript: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '4px',
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '12px',
  },
  botRow: {
    display: 'flex',
    width: '100%',
  },
  botBubble: {
    maxWidth: '78%',
    backgroundColor: '#f5f4f0',
    border: '1px solid #ececec',
    borderRadius: '14px 14px 14px 4px',
    padding: '8px 12px',
  },
  botUserBubble: {
    maxWidth: '78%',
    backgroundColor: '#7a9471',
    borderRadius: '14px 14px 4px 14px',
    padding: '8px 12px',
  },
  botBubbleText: {
    fontSize: '13px',
    lineHeight: 1.45,
    color: '#1a1a1a',
    whiteSpace: 'pre-wrap',
    WebkitTextFillColor: 'currentColor',
  },
  botUserText: {
    color: '#ffffff',
    WebkitTextFillColor: '#ffffff',
  },
  botBubbleTime: {
    fontSize: '10px',
    opacity: 0.55,
    marginTop: '4px',
  },
});

export default AvailableLeadModal;